import { useEffect, useState, type KeyboardEvent } from 'react';

import type { PaletteCommand } from '../commandPalette.types';

/**
 * One highlighted row across every section. Arrows move it and wrap at the
 * ends; Enter runs it. Focus never leaves the input, so typing keeps working
 * while the highlight moves.
 *
 * The highlight returns to the top whenever the list itself changes — a new
 * keystroke or a new set of results — so Enter never runs a row that has
 * scrolled away from what the viewer was looking at.
 */
export function usePaletteHighlight(rows: PaletteCommand[], run: (row: PaletteCommand) => void) {
  const [active, setActive] = useState(0);
  const rowKey = rows.map((r) => r.id).join('|');

  useEffect(() => setActive(0), [rowKey]);

  const activeRow: PaletteCommand | undefined = rows[active];

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (rows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % rows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + rows.length) % rows.length);
    } else if (e.key === 'Enter' && activeRow) {
      e.preventDefault();
      run(activeRow);
    }
  }

  return {
    activeId: activeRow?.id ?? null,
    setActiveId: (id: string) => setActive(rows.findIndex((r) => r.id === id)),
    onKeyDown,
  };
}
